import { useMemo, useState } from "react";
import classNames from "classnames";
import { format, parseISO } from "date-fns";
import { Ledger } from "@common/contracts/ledger.contract";
import { PREMIUM_FEATURES } from "@common/flatRateBilling";
import {
    AccountingLedgerBasis,
    AccountingLedgerStatus,
} from "@common/types/domains/accounting/ledger";
import { Entity } from "@common/types/entity";
import { useLedgersByEntityIds, useSetLedgerStatus } from "@api/ledger.api";
import { EntityDisplay } from "@components/entity/EntityDisplay";
import { Button } from "@design-system/Button/Button";
import { ConfirmationModal } from "@design-system/Modal/ConfirmationModal";
import { FeatureAccess } from "@components/settings/FeatureAccess/FeatureAccess";
import { Menu } from "@design-system/Menu/Menu.lib";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { Section } from "@design-system/Section/Section";
import { LedgerFormModal } from "./LedgerFormModal";
import { NewLedgerModal } from "./NewLedgerModal";
import styles from "./AccountingSettings.module.scss";
import { Tooltip } from "@design-system/Tooltip/Tooltip";

interface Props {
    entities: Entity[];
    canEditSettings: boolean;
}

export const MultiLedgerSettingsSection: React.FC<Props> = ({
    entities,
    canEditSettings,
}) => {
    const [selectedEntityId, setSelectedEntityId] = useState<
        Entity["id"] | undefined
    >();
    const [isNewLedgerModalOpen, setIsNewLedgerModalOpen] = useState(false);
    const [ledgerToEdit, setLedgerToEdit] = useState<Ledger | undefined>();
    const [ledgerToDuplicate, setLedgerToDuplicate] = useState<
        Ledger | undefined
    >();
    const [ledgerStatusAction, setLedgerStatusAction] = useState<
        Ledger | undefined
    >();
    const setLedgerStatus = useSetLedgerStatus();
    const { error: errorToast } = useToaster();
    const entityIds = useMemo(
        () => entities.map((entity) => entity.id),
        [entities],
    );
    const { data: ledgersByEntityId = {}, isLoading } = useLedgersByEntityIds(
        entityIds,
        true,
    );

    const handleOpenNewLedger = (entityId: Entity["id"]) => {
        setSelectedEntityId(entityId);
        setIsNewLedgerModalOpen(true);
    };

    return (
        <Section heading="Ledgers">
            {entities.length === 0 ? (
                <p>No business entities available.</p>
            ) : (
                <div className={styles.ledgerGroups}>
                    {entities.map((entity) => (
                        <EntityLedgerGroup
                            key={entity.id}
                            entity={entity}
                            ledgers={ledgersByEntityId[entity.id] ?? []}
                            isLoading={isLoading}
                            canEditSettings={canEditSettings}
                            onNewLedger={handleOpenNewLedger}
                            onEditLedger={setLedgerToEdit}
                            onDuplicateLedger={setLedgerToDuplicate}
                            onChangeLedgerStatus={setLedgerStatusAction}
                        />
                    ))}
                </div>
            )}
            <NewLedgerModal
                entities={entities}
                initialEntityId={selectedEntityId}
                show={isNewLedgerModalOpen}
                onHide={() => setIsNewLedgerModalOpen(false)}
            />
            <LedgerFormModal
                mode="edit"
                entities={entities}
                ledger={ledgerToEdit}
                show={!!ledgerToEdit}
                onHide={() => setLedgerToEdit(undefined)}
            />
            <LedgerFormModal
                mode="duplicate"
                entities={entities}
                ledger={ledgerToDuplicate}
                show={!!ledgerToDuplicate}
                onHide={() => setLedgerToDuplicate(undefined)}
            />
            <ConfirmationModal
                show={!!ledgerStatusAction}
                title={getStatusActionTitle(ledgerStatusAction)}
                question={getStatusActionQuestion(
                    ledgerStatusAction,
                    ledgerStatusAction
                        ? (ledgersByEntityId[ledgerStatusAction.entityId] ?? [])
                        : [],
                )}
                yes={getStatusActionLabel(ledgerStatusAction)}
                yesBtnVariant={
                    ledgerStatusAction?.status === AccountingLedgerStatus.active
                        ? "destructive"
                        : "primary"
                }
                no="Cancel"
                onReject={() => setLedgerStatusAction(undefined)}
                onHide={() => setLedgerStatusAction(undefined)}
                onConfirm={async () => {
                    if (!ledgerStatusAction) {
                        return;
                    }
                    try {
                        await setLedgerStatus.mutateAsync({
                            params: {
                                entityId: ledgerStatusAction.entityId,
                                ledgerId: ledgerStatusAction.id,
                            },
                            body: {
                                status:
                                    ledgerStatusAction.status ===
                                    AccountingLedgerStatus.active
                                        ? AccountingLedgerStatus.archived
                                        : AccountingLedgerStatus.active,
                            },
                        });
                        setLedgerStatusAction(undefined);
                    } catch {
                        errorToast("Failed to update ledger status");
                    }
                }}
            />
        </Section>
    );
};

interface EntityLedgerGroupProps {
    entity: Entity;
    ledgers: Ledger[];
    isLoading: boolean;
    canEditSettings: boolean;
    onNewLedger: (entityId: Entity["id"]) => void;
    onEditLedger: (ledger: Ledger) => void;
    onDuplicateLedger: (ledger: Ledger) => void;
    onChangeLedgerStatus: (ledger: Ledger) => void;
}

const EntityLedgerGroup: React.FC<EntityLedgerGroupProps> = ({
    entity,
    ledgers,
    isLoading,
    canEditSettings,
    onNewLedger,
    onEditLedger,
    onDuplicateLedger,
    onChangeLedgerStatus,
}) => {
    const sortedLedgers = useMemo(
        () =>
            [...ledgers].sort((first, second) => {
                if (first.status !== second.status) {
                    return first.status === AccountingLedgerStatus.active
                        ? -1
                        : 1;
                }
                if (first.isPrimary !== second.isPrimary) {
                    return first.isPrimary ? -1 : 1;
                }
                return first.name.localeCompare(second.name);
            }),
        [ledgers],
    );

    return (
        <section className={styles.ledgerGroup}>
            <header className={styles.ledgerGroupHeader}>
                <EntityDisplay entity={entity} />
                <FeatureAccess
                    feature={PREMIUM_FEATURES.ACCRUAL_LEDGER}
                    renderAllowed={() => (
                        <Button
                            variant="primary"
                            size="md"
                            type="button"
                            onClick={() => onNewLedger(entity.id)}
                            disabled={!canEditSettings}
                        >
                            + New ledger
                        </Button>
                    )}
                    renderBlocked={({ openPlans, canManageBilling }) => (
                        <Tooltip content="Upgrade to Plus to create ledgers">
                            <Button
                                variant="primary"
                                size="md"
                                type="button"
                                onClick={
                                    canManageBilling ? openPlans : undefined
                                }
                                disabled={!canManageBilling}
                            >
                                + New ledger
                            </Button>
                        </Tooltip>
                    )}
                />
            </header>
            <table className={styles.ledgerTable}>
                <thead>
                    <tr>
                        <th>Ledger name</th>
                        <th>Method</th>
                        <th>Status</th>
                        <th>Created</th>
                        <th aria-label="Actions" />
                    </tr>
                </thead>
                <tbody>
                    {sortedLedgers.map((ledger) => (
                        <LedgerRow
                            key={ledger.id}
                            ledger={ledger}
                            canEditSettings={canEditSettings}
                            onEditLedger={onEditLedger}
                            onDuplicateLedger={onDuplicateLedger}
                            onChangeLedgerStatus={onChangeLedgerStatus}
                        />
                    ))}
                    {!isLoading && sortedLedgers.length === 0 && (
                        <tr>
                            <td colSpan={5} className={styles.emptyLedgerCell}>
                                No ledgers created.
                            </td>
                        </tr>
                    )}
                    {isLoading && (
                        <tr>
                            <td colSpan={5} className={styles.emptyLedgerCell}>
                                Loading ledgers...
                            </td>
                        </tr>
                    )}
                </tbody>
            </table>
        </section>
    );
};

interface LedgerRowProps {
    ledger: Ledger;
    canEditSettings: boolean;
    onEditLedger: (ledger: Ledger) => void;
    onDuplicateLedger: (ledger: Ledger) => void;
    onChangeLedgerStatus: (ledger: Ledger) => void;
}

const LedgerRow: React.FC<LedgerRowProps> = ({
    ledger,
    canEditSettings,
    onEditLedger,
    onDuplicateLedger,
    onChangeLedgerStatus,
}) => (
    <tr
        className={classNames({
            [styles.archivedLedgerRow]:
                ledger.status === AccountingLedgerStatus.archived,
        })}
    >
        <td>
            <span className={styles.ledgerName}>{ledger.name}</span>
            {ledger.isPrimary &&
                ledger.status === AccountingLedgerStatus.active && (
                    <span className={styles.primaryBadge}>Primary</span>
                )}
        </td>
        <td>{getBasisLabel(ledger.basis)}</td>
        <td>{getStatusLabel(ledger.status)}</td>
        <td>{format(parseISO(String(ledger.createdAt)), "MMM d, yyyy")}</td>
        <td className={styles.ledgerActionsCell}>
            <Menu.More disabled={!canEditSettings}>
                <Menu.Item onClick={() => onEditLedger(ledger)}>Edit</Menu.Item>
                <Menu.Item
                    onClick={() => onChangeLedgerStatus(ledger)}
                    destructive={
                        ledger.status === AccountingLedgerStatus.active
                    }
                >
                    {ledger.status === AccountingLedgerStatus.active
                        ? "Archive"
                        : "Activate"}
                </Menu.Item>
                <Menu.Item onClick={() => onDuplicateLedger(ledger)}>
                    Duplicate
                </Menu.Item>
            </Menu.More>
        </td>
    </tr>
);

function getBasisLabel(basis: AccountingLedgerBasis): string {
    return basis === AccountingLedgerBasis.cash ? "Cash" : "Accrual";
}

function getStatusLabel(status: AccountingLedgerStatus): string {
    return status === AccountingLedgerStatus.active ? "Active" : "Archived";
}

function getStatusActionTitle(ledger?: Ledger): string {
    if (!ledger) {
        return "";
    }
    return ledger.status === AccountingLedgerStatus.active
        ? `Archive "${ledger.name}"?`
        : `Activate "${ledger.name}"?`;
}

function getStatusActionLabel(ledger?: Ledger): string {
    return ledger?.status === AccountingLedgerStatus.archived
        ? "Activate"
        : "Archive";
}

function getStatusActionQuestion(
    ledger?: Ledger,
    siblingLedgers: Ledger[] = [],
): string {
    if (!ledger) {
        return "";
    }
    if (ledger.status === AccountingLedgerStatus.archived) {
        return "This ledger will become available in accounting report and manual journal dropdowns again.";
    }
    const hasReplacement = siblingLedgers.some(
        (sibling) =>
            sibling.id !== ledger.id &&
            sibling.basis === ledger.basis &&
            sibling.status === AccountingLedgerStatus.active,
    );
    if (ledger.isPrimary && hasReplacement) {
        return "No further entries will be generated on this ledger, and another active ledger with the same method will become primary. You can restore it later if needed.";
    }
    return "No further entries will be generated on this ledger, and historical entries will be locked. You can restore it later if needed.";
}
